import { useMemo, useState } from 'react';
import { Bell, Check, ChevronUp, CloudSun, MessageSquareText } from 'lucide-react';
import { resolveOutlookPeriods } from '../../lib/forecastOutlook';
import type { ObservedWeatherLayerKey } from '../../lib/observedWeather';
import type {
  ForecastDaySummary,
  ForecastHourSummary,
  ForecastVariable,
} from './WeatherDashboard';
import { WeatherHistoricalClimate } from './WeatherHistoricalClimate';
import { WeatherOutlookChart, type OutlookChartMode } from './WeatherOutlookChart';
import {
  dayHiLoFromHours,
  filterHoursForDay,
} from './weatherChartUtils';
import { WEATHER_MUTED } from './weatherDesign';
import { WeatherCodeIcon } from './WeatherCodeIcon';
import { WeatherConditionsWarning } from './WeatherConditionsWarning';
import { weatherCodeLabel, weekdayLong, weekdayShort } from './weatherLabels';
import { WEATHER_VARIABLES } from './weatherVariables';
import {
  TOUR_PANEL_SHELL,
  TourStylePanelMiniHeader,
  TourStylePanelShell,
  TOUR_PANEL_HEADER_BTN,
  tourSegmentedBtn,
  TOUR_SEGMENTED_TRACK,
} from '../ui/TourStylePanelShell';

interface WeatherOutlookPanelProps {
  days: ForecastDaySummary[];
  hours: ForecastHourSummary[];
  loading: boolean;
  activeVariable: ForecastVariable;
  selectedPeriod: string | null;
  availablePeriods: string[];
  districtName?: string | null;
  unavailableMessage?: string | null;
  summaryHidden?: boolean;
  onOpenSummary?: () => void;
  onSubscribe?: () => void;
  isSubscribed?: boolean;
  onVariableChange: (variable: ForecastVariable) => void;
  onPeriodChange: (period: string) => void;
  climateActiveLayer: ObservedWeatherLayerKey | null;
  climateSelectedMonths: string[];
  onClimateLayerSelect: (layer: ObservedWeatherLayerKey | null) => void;
  onClimateMonthToggle: (period: string, enabled: boolean) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const TAB_LABELS: Record<ForecastVariable, string> = {
  temp: 'Temperature',
  precip: 'Precipitation',
  wind: 'Wind',
};

const TAB_LABELS_SHORT: Record<ForecastVariable, string> = {
  temp: 'Temp',
  precip: 'Rain',
  wind: 'Wind',
};

const VARIABLE_UNDERLINE: Record<ForecastVariable, string> = {
  temp: 'bg-amber-400',
  precip: 'bg-sky-400',
  wind: 'bg-violet-400',
};

function avgHumidity(hours: ForecastHourSummary[], period: string): number | null {
  const values = filterHoursForDay(hours, period)
    .map((h) => h.relative_humidity_pct)
    .filter((v): v is number => v != null);
  if (values.length === 0) return null;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

export function WeatherOutlookPanel({
  days,
  hours,
  loading,
  activeVariable,
  selectedPeriod,
  availablePeriods,
  districtName,
  unavailableMessage = null,
  summaryHidden = false,
  onOpenSummary,
  onSubscribe,
  isSubscribed = false,
  onVariableChange,
  onPeriodChange,
  climateActiveLayer,
  climateSelectedMonths,
  onClimateLayerSelect,
  onClimateMonthToggle,
  collapsed,
  onCollapsedChange,
}: WeatherOutlookPanelProps) {
  const periods = resolveOutlookPeriods(availablePeriods);
  const todayPeriod = periods[0] ?? days[0]?.period ?? null;
  const focusPeriod = selectedPeriod ?? todayPeriod;
  const [chartMode, setChartMode] = useState<OutlookChartMode>('week');

  const today = days[0];

  const focusDay = days.find((d) => d.period === focusPeriod) ?? days[0];
  const headerDay = chartMode === 'day' ? focusDay : days[0];
  const headerPeriod = chartMode === 'day' ? focusPeriod : todayPeriod;

  const hiLo = useMemo(
    () => (headerPeriod ? dayHiLoFromHours(hours, headerPeriod) : null),
    [hours, headerPeriod],
  );

  const humidity = headerPeriod ? avgHumidity(hours, headerPeriod) : null;
  const forecastUnavailable = Boolean(unavailableMessage);
  const showSkeleton = loading && days.length === 0 && !forecastUnavailable;

  const handleDayClick = (period: string) => {
    onPeriodChange(period);
    setChartMode('day');
  };

  const handleWeekView = () => {
    setChartMode('week');
    if (todayPeriod) onPeriodChange(todayPeriod);
  };

  const headerActions = (
    <>
      {onSubscribe && !forecastUnavailable && (
        isSubscribed ? (
          <span
            className={`${TOUR_PANEL_HEADER_BTN} cursor-default bg-white/20`}
            title="You are already subscribed to daily weather emails for this area"
            aria-label="Subscribed to daily weather briefing"
          >
            <Check size={12} className="shrink-0" aria-hidden />
            <span className="hidden min-[420px]:inline">Subscribed</span>
            <span className="min-[420px]:hidden">Subscribed</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={onSubscribe}
            aria-label="Subscribe for daily weather forecast"
            className={TOUR_PANEL_HEADER_BTN}
          >
            <Bell size={12} className="shrink-0" aria-hidden />
            <span className="hidden min-[480px]:inline">Daily forecast emails</span>
            <span className="min-[480px]:hidden">Subscribe</span>
          </button>
        )
      )}
      {summaryHidden && onOpenSummary && (
        <button
          type="button"
          onClick={onOpenSummary}
          aria-label="Show forecast summary"
          className={TOUR_PANEL_HEADER_BTN}
        >
          <MessageSquareText size={12} strokeWidth={2.25} aria-hidden />
          Summary
        </button>
      )}
    </>
  );

  if (collapsed) {
    return (
      <div className={`relative z-[1] mx-2 mb-2 sm:mx-3 sm:mb-3 ${TOUR_PANEL_SHELL}`}>
        <button
          type="button"
          onClick={() => onCollapsedChange(false)}
          className="flex w-full cursor-pointer flex-col text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/40 touch-manipulation"
          aria-expanded={false}
          aria-label="Expand weather outlook"
        >
          <TourStylePanelMiniHeader
            headerIcon={<CloudSun size={14} />}
            headerLabel="Weather outlook"
            subtitle={forecastUnavailable ? 'Forecast unavailable' : districtName}
            trailing={
              <span className="flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-white/90">
                Tap to expand
                <ChevronUp size={12} aria-hidden />
              </span>
            }
          />
          <div className="flex w-full items-center gap-2.5 px-3 py-2.5 sm:gap-3">
            {forecastUnavailable ? (
              <div className="min-w-0 flex-1">
                <WeatherConditionsWarning message={unavailableMessage!} />
              </div>
            ) : (
              <>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-950/40 dark:to-cyan-950/30 ring-1 ring-emerald-200/60 dark:ring-emerald-800/40">
              <WeatherCodeIcon code={today?.weather_code} size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">Today</span>
                {today?.temperature_c != null && (
                  <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    {Math.round(today.temperature_c)}°
                  </span>
                )}
                {WEATHER_VARIABLES.map(({ id, label, icon, iconClass, readShort, pillClass }) => {
                  const active = activeVariable === id;
                  return (
                    <span
                      key={id}
                      className={`inline-flex items-center gap-1 rounded-lg border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${pillClass} ${
                        active
                          ? 'border-emerald-400/80 ring-1 ring-emerald-500/40 dark:border-emerald-600'
                          : 'border-gray-200/80 dark:border-gray-700'
                      }`}
                    >
                      <span className={`flex h-3.5 w-3.5 items-center justify-center ${iconClass}`}>
                        {icon}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">{readShort(today)}</span>
                      <span className="sr-only">{label}</span>
                    </span>
                  );
                })}
              </span>
            </span>
              </>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="relative z-[1] mx-2 mb-2 sm:mx-3 sm:mb-3">
      <TourStylePanelShell
        headerIcon={<CloudSun size={16} />}
        headerLabel="Weather outlook"
        subtitle={
          forecastUnavailable
            ? 'Forecast unavailable'
            : loading && !districtName
              ? undefined
              : districtName
        }
        onCollapse={() => onCollapsedChange(true)}
        collapseLabel="Minimise weather outlook to see more of the map"
        headerActions={headerActions}
        footer={
          forecastUnavailable ? undefined : (
            <WeatherHistoricalClimate
              activeLayer={climateActiveLayer}
              selectedMonths={climateSelectedMonths}
              onSelectLayer={onClimateLayerSelect}
              onToggleMonth={onClimateMonthToggle}
            />
          )
        }
      >
        {forecastUnavailable ? (
          <div className="px-3 py-3 sm:px-4">
            <WeatherConditionsWarning message={unavailableMessage!} />
          </div>
        ) : showSkeleton ? (
          <div className="space-y-3 px-3 py-3 sm:px-4">
            <div className="flex gap-3">
              <span className="h-14 w-14 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <span className="block h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <span className="block h-3 w-40 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
              </div>
            </div>
            <span className="block h-[132px] animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-950/40 dark:to-cyan-950/30 ring-1 ring-emerald-200/60 dark:ring-emerald-800/40">
                <WeatherCodeIcon code={headerDay?.weather_code} size={30} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <p className="text-4xl font-light tabular-nums leading-none text-gray-900 dark:text-white">
                    {headerDay?.temperature_c != null ? Math.round(headerDay.temperature_c) : '—'}
                    <span className="ml-0.5 text-xl font-normal text-gray-500 dark:text-gray-400">°</span>
                  </p>
                  {hiLo && (
                    <p className={`${WEATHER_MUTED} tabular-nums`}>
                      H {Math.round(hiLo.hi)}° · L {Math.round(hiLo.lo)}°
                    </p>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                  {headerDay?.precipitation_mm != null && (
                    <span className={`${WEATHER_MUTED} tabular-nums`}>
                      Rain {headerDay.precipitation_mm.toFixed(0)} mm
                    </span>
                  )}
                  {humidity != null && (
                    <span className={`${WEATHER_MUTED} tabular-nums`}>Humidity {humidity}%</span>
                  )}
                  {headerDay?.wind_speed_ms != null && (
                    <span className={`${WEATHER_MUTED} tabular-nums`}>
                      Wind {headerDay.wind_speed_ms.toFixed(1)} m/s
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden shrink-0 text-right sm:block">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                  Weather
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {headerPeriod ? weekdayLong(headerPeriod) : '—'}
                </p>
                <p className={`mt-0.5 ${WEATHER_MUTED}`}>{weatherCodeLabel(headerDay?.weather_code)}</p>
              </div>
            </div>

            <div className="flex min-h-10 items-center gap-0.5 overflow-x-auto border-y border-gray-200/80 px-2 dark:border-gray-700/80 sm:gap-1 sm:px-4">
              {WEATHER_VARIABLES.map(({ id }) => {
                const active = activeVariable === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onVariableChange(id)}
                    aria-pressed={active}
                    className={`relative flex h-10 shrink-0 items-center px-2 text-[11px] font-medium transition-colors sm:px-2.5 sm:text-xs ${
                      active
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    <span className="sm:hidden">{TAB_LABELS_SHORT[id]}</span>
                    <span className="hidden sm:inline">{TAB_LABELS[id]}</span>
                    {active && (
                      <span
                        className={`absolute inset-x-1 bottom-1.5 h-0.5 rounded-full ${VARIABLE_UNDERLINE[id]}`}
                      />
                    )}
                  </button>
                );
              })}
              <div
                className={`${TOUR_SEGMENTED_TRACK} ml-auto shrink-0`}
                role="group"
                aria-label="Chart time range"
              >
                <button
                  type="button"
                  onClick={handleWeekView}
                  aria-pressed={chartMode === 'week'}
                  className={tourSegmentedBtn(chartMode === 'week')}
                >
                  Week
                </button>
                <button
                  type="button"
                  onClick={() => focusPeriod && setChartMode('day')}
                  disabled={!focusPeriod}
                  aria-pressed={chartMode === 'day'}
                  className={`${tourSegmentedBtn(chartMode === 'day')} disabled:opacity-40`}
                >
                  Day
                </button>
              </div>
            </div>

            <div className="px-2 pt-1 sm:px-4">
              <WeatherOutlookChart
                variable={activeVariable}
                mode={chartMode}
                days={days}
                hours={hours}
                periods={periods}
                selectedPeriod={focusPeriod}
                loading={loading}
              />
            </div>

            <div className="flex gap-1.5 overflow-x-auto px-3 py-3 sm:px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {periods.map((period) => {
                const day = days.find((d) => d.period === period);
                const selected = period === focusPeriod;
                const dayHiLo = dayHiLoFromHours(hours, period);
                const isToday = period === todayPeriod;

                return (
                  <button
                    key={period}
                    type="button"
                    onClick={() => handleDayClick(period)}
                    aria-pressed={selected}
                    aria-label={`${weekdayLong(period)} forecast${selected ? ', selected' : ''}`}
                    className={`flex min-w-[3.25rem] shrink-0 flex-col items-center gap-1 rounded-xl border px-2 py-2 transition-all ${
                      selected
                        ? 'border-emerald-400 bg-gradient-to-b from-emerald-50 to-white shadow-sm ring-2 ring-emerald-500/30 dark:border-emerald-600 dark:from-emerald-950/40 dark:to-gray-900 dark:ring-emerald-500/25'
                        : 'border-gray-200/80 bg-gray-50/80 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span
                      className={`text-[10px] font-semibold ${
                        selected
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {isToday ? 'Today' : weekdayShort(period)}
                    </span>
                    <span className="flex h-6 w-6 items-center justify-center opacity-90">
                      <WeatherCodeIcon code={day?.weather_code} size={18} />
                    </span>
                    <span className="text-[10px] font-semibold tabular-nums leading-tight text-gray-900 dark:text-gray-100">
                      {dayHiLo ? (
                        <>
                          {Math.round(dayHiLo.hi)}°{' '}
                          <span className="font-normal text-gray-500 dark:text-gray-400">
                            {Math.round(dayHiLo.lo)}°
                          </span>
                        </>
                      ) : day?.temperature_c != null ? (
                        `${Math.round(day.temperature_c)}°`
                      ) : (
                        '—'
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </TourStylePanelShell>
    </div>
  );
}
